import React, {
  FC,
  Fragment,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  RefObject,
  InputHTMLAttributes,
} from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import { Product, Option, Feature, Modals, PlanCardsMetadata, SelectedProductsState } from '~/types/products';
import { Buttons, Card } from '~/types/preamp';
import FlipCard from './FlipCard';
import PlanCardStyled from './PlanCardStyled';
import PlanCardCTAs from './components/PlanCardCTAs';
import PlanCardBanner from './components/PlanCardBanner';
import FeatureItem from './components/FeatureItem';
import { ChevronDown, ChevronLeft, ChevronRight, InfoCircle } from '../../Icons/fill';
import Star from '../../Icons/outline/Star';
import { trackEvent } from '~/helpers/tracking';
import addMoney from '~/helpers/addMoney';
import slugify from '~/helpers/slugify';
import { throttle } from 'lodash';
import { getProductSpeed } from '../../../helpers/products';
import Tooltip from '~/components/Tooltip';
import { getTooltip } from '~/helpers/legal';
import ProductFilters from '~/components/ProductFilters/components/ProductFilters';
import { ReactSelectOption } from '~/types/common';
import setHtml from '~/helpers/setHtml';
import Cookies from 'js-cookie';
import { iconMap } from '~/constants/iconMap';
import getShortLegal from '~/helpers/getShortLegal';
import { totalValueDetails } from './data';
import priceFormatter from '~/helpers/priceFormatter';
import getAsset from '~/helpers/getAsset';
import { NETFLIX_BUNDLE } from '~/constants/entityIds';
import useIsInViewport from '~/hooks/useInViewport';
import LegalAnnotation from '~/components/LegalAnnotation';

interface Props {
  index?: number;
  metadata?: PlanCardsMetadata;
  addPhoneState: boolean;
  bundleCost: number;
  bundleFeatures: Feature[];
  product: Product;
  singleProduct: boolean;
  sweetener: Option;
  isCopper: boolean;
  is200: boolean;
  is500: boolean;
  is2Gig: boolean;
  is5Gig: boolean;
  is7Gig: boolean;
  is1Gig: boolean;
  isCopper2Fiber: boolean;
  selectedState?: SelectedProductsState;
  handleSelectPlan?: (product: Product) => void;
  setActiveModal: (key: Modals) => void;
  setVasModal: (product: Product) => void;
  setEeroModal: (product: Product) => void;
  setBroadbandConsumerLabelModal: (product: Product) => void;
  buttons: Buttons;
  card: Card;
  selectedCard: boolean;
  setSelectedCardIndex: (value: number) => void;
  maxLegalHeight?: number;
  maxLegalHeightRef?: RefObject<number[]>;
  setMaxLegalHeight?: (value: number) => void;
  maxFeatureCollapsedHeight?: number;
  maxFeatureCollapsedHeightRef?: RefObject<number[]>;
  setMaxFeatureCollapsedHeight?: (value: number) => void;
  maxFeatureExpandedHeight?: number;
  maxFeatureExpandedHeightRef?: RefObject<number[]>;
  setMaxFeatureExpandedHeight?: (value: number) => void;
  productFilters?: {
    options: ReactSelectOption[];
    setOptions: (options: ReactSelectOption[]) => void;
    sweetener: ReactSelectOption;
    setSweetener: (selected: ReactSelectOption) => void;
    voiceAvailable: boolean;
    handleAddHomePhone: (event: InputHTMLAttributes<HTMLInputElement>) => void;
    handleAddProductState: (product: Product) => void;
    addPhoneState: boolean;
  };
  // Setting this enables element viewed tracking for the entire product card
  cardTracking?: {
    position: string;
    location: string;
  };
  showPlanCardBanner: boolean;
}

const PlanCard: FC<Props> = ({
  index,
  metadata,
  bundleCost: bundCost,
  bundleFeatures,
  product,
  sweetener,
  selectedState,
  handleSelectPlan,
  setVasModal,
  setEeroModal,
  setBroadbandConsumerLabelModal,
  isCopper,
  buttons,
  card,
  selectedCard,
  setSelectedCardIndex,
  maxLegalHeight,
  maxLegalHeightRef,
  setMaxLegalHeight,
  maxFeatureCollapsedHeight,
  maxFeatureCollapsedHeightRef,
  setMaxFeatureCollapsedHeight,
  maxFeatureExpandedHeight,
  maxFeatureExpandedHeightRef,
  setMaxFeatureExpandedHeight,
  productFilters,
  cardTracking,
  showPlanCardBanner,
}) => {
  const { products, legal, callInNumber, preamp, quiz, address, monarch } = useSelector((store: ReduxState) => store);
  const { legalMap = {} } = legal || {};
  const netflixWifiBundleToggled = products?.planPageSelections?.selectedNetflix;
  const netflixWifiBundle = products?.available?.video?.find((product) => product.productId === NETFLIX_BUNDLE);
  const {
    dataspeed,
    features: productFeatures,
    is200,
    is500,
    is1Gig,
    is2Gig,
    is5Gig,
    is7Gig,
    name,
    price,
    productId,
    shortLegal,
    sweeteners,
  } = product;

  const featuredTheme = card?.theme || 'theme-2';
  const [selectedSpeed] = card?.featuredSpeed?.split('|') ?? [];
  const featuredCard = getProductSpeed(product) === `is${selectedSpeed}`;

  const {
    bestFor,
    bannerDetails,
    innerBanner,
    bannerEnabled: enabled,
    featureList: list = [],
    useBulletsWithFeatureList,
    totalValueEnabled,
  } = metadata?.[dataspeed?.downloadInMbps] || {};

  const selectedPlan = (selectedState?.internet?.productId ?? products?.selected?.internet?.productId) === productId;
  const bundleCost = is2Gig || preamp?.VASVersion ? 0 : bundCost;
  const addressOfferType = Cookies.get('addressOfferType') ?? 'default';

  const bannerEnabled = enabled !== 'false' && showPlanCardBanner;
  const featureList = list
    ?.map((item, index) => {
      if (item.text !== '__EMPTY__') {
        const legal = item.legal === '__EMPTY__' ? '' : item.legal;
        // Override the first RTB for the netflix bundle product cards
        if (netflixWifiBundleToggled && !is7Gig && index === 0) {
          return {
            text: 'Netflix & Pro Wi-Fi Bundle',
          };
        }
        return {
          text: item.text,
          legal,
        };
      }
    })
    .filter(Boolean);

  const features = (() => {
    if (useBulletsWithFeatureList && featureList?.length) return featureList; // Use features from Preamp
    if (bundleFeatures && productFeatures) return [...productFeatures, ...bundleFeatures]; // Use features from OL + bundle features
    if (productFeatures && !bundleFeatures) return productFeatures; // Use features from OL
    return [];
  })();

  const Icon = iconMap[metadata?.[dataspeed?.downloadInMbps]?.bannerDetails?.bannerIcon] || 'none';
  const IconInnerBanner = iconMap[metadata?.[dataspeed?.downloadInMbps]?.innerBanner?.innerBannerIcon] || 'none';
  const hasFeatureSections = bestFor?.length && featureList?.length;
  const isFlipCard = String(featuredTheme) === 'theme-4';

  const toggleVisibility = (type: 'features' | 'includedValues') => {
    let newState, actionDetail;

    if (isFlipCard) {
      setFlipped(!flipped);
      newState = !flipped;
      actionDetail = 'flip';
    } else if (type === 'features') {
      setExpanded(!expanded);
      newState = !expanded;
      actionDetail = 'more';
    } else if (type === 'includedValues') {
      setExpandedIncludedValue(!expandedIncludedValue);
      newState = !expandedIncludedValue;
      actionDetail = 'included_value';
    }

    trackEvent({
      action: 'elementClicked',
      data: {
        text: `${name} Internet`,
        elementType: 'BUTTON',
        location: 'product_selection_page',
        position: `product_cards_plan_details_${newState ? 'open' : 'closed'}_${actionDetail}`,
      },
    });
  };

  useEffect(() => {
    if (selectedPlan) {
      setSelectedCardIndex(index);
    }
  }, [selectedPlan]);

  /* Product Card Element Viewed Tracking
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  const cardRef = useRef();
  const inViewport = useIsInViewport(cardRef, 0.35);
  const [hasViewedEvent, setHasViewedEvent] = useState(false);

  useEffect(() => {
    if (inViewport && cardTracking && !hasViewedEvent) {
      const { position, location } = cardTracking;
      setHasViewedEvent(true);
      trackEvent({
        action: 'elementViewed',
        data: {
          elementType: 'Product Card',
          location: location,
          position: position,
          text: product?.name,
        },
      });
    }
  }, [inViewport]);

  /* Equal Height: Legal
  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  const legalWrapperRef = useRef(null) as RefObject<HTMLDivElement>;
  const legalRef = useRef(null) as RefObject<HTMLDivElement>;

  const resizeLegal = useCallback(() => {
    if (!maxLegalHeightRef) return;
    const height = Math.ceil(legalRef?.current?.getBoundingClientRect().height);
    maxLegalHeightRef.current[index] = height;
    setMaxLegalHeight(Math.max(...maxLegalHeightRef?.current));
    if (legalWrapperRef.current) legalWrapperRef.current.style.height = `${maxLegalHeight}px`;
  }, [index, maxLegalHeight, maxLegalHeightRef, setMaxLegalHeight]);

  useEffect(resizeLegal, [resizeLegal]);

  /* Equal Height: Featured List
  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
  const [expanded, setExpanded] = useState(Boolean(quiz?.results));
  const [expandedIncludedValue, setExpandedIncludedValue] = useState(Boolean(quiz?.results));
  const [flipped, setFlipped] = useState(false);
  const featureRef = useRef(null);

  const resizeFeatureList = useCallback(() => {
    if (!maxFeatureExpandedHeightRef) return;

    const height = Math.ceil(featureRef?.current?.getBoundingClientRect().height);
    maxFeatureExpandedHeightRef.current[index] = height;
    setMaxFeatureExpandedHeight(Math.max(...maxFeatureExpandedHeightRef?.current));

    const paddingBottom =
      parseInt(
        (featureRef?.current?.currentStyle || window.getComputedStyle(featureRef?.current))?.paddingBottom,
        10
      ) || 0;

    const visibleHeight =
      Math.ceil(
        featureRef?.current?.querySelector('.hidden')?.getBoundingClientRect().top -
          featureRef?.current?.getBoundingClientRect().top +
          paddingBottom
      ) || 0;

    maxFeatureCollapsedHeightRef.current[index] = hasFeatureSections ? 0 : visibleHeight;
    setMaxFeatureCollapsedHeight(Math.max(...maxFeatureCollapsedHeightRef?.current));
  }, [
    index,
    expanded,
    expandedIncludedValue,
    maxFeatureExpandedHeightRef,
    setMaxFeatureExpandedHeight,
    maxFeatureCollapsedHeightRef,
    setMaxFeatureCollapsedHeight,
    featureRef,
    hasFeatureSections,
  ]);

  useEffect(resizeFeatureList, [resizeFeatureList]);

  useLayoutEffect(() => {
    const throttleResize = throttle(() => {
      resizeLegal();
      resizeFeatureList();
    }, 100);
    window.addEventListener('resize', throttleResize);
    return () => window.removeEventListener('resize', throttleResize);
  }, [resizeLegal, resizeFeatureList]);

  const indianaOffer = address?.install?.dma === '509' && monarch?.indianaOffer;
  // Allows for editable content, but will not require editable assets
  // specific to Indiana offer. Indiana overrides text from metadata.
  const outerBannerText = () => {
    if (indianaOffer) return 'FREE Whole-Home Wi-Fi<br />for 3 months';
    return metadata?.[dataspeed?.downloadInMbps]?.outerBanner?.outerBannerText;
  };

  const innerBannerText = () => {
    return metadata?.[dataspeed?.downloadInMbps]?.innerBanner?.innerBannerText;
  };

  const outerBannerBackground = metadata?.[dataspeed?.downloadInMbps]?.outerBanner?.outerBannerBackground || 'gravity';
  const innerBannerBackground = metadata?.[dataspeed?.downloadInMbps]?.innerBanner?.innerBannerBackground || 'gravity';

  const handleLearnMoreClick = () => {
    setVasModal(netflixWifiBundle);

    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'NETFLIX_BUNDLE_LEARN_MORE_MODAL',
        location: 'product_selection_page',
        position: 'product_cards',
        text: 'Netflix Pro Wifi Bundle Learn More Button Clicked',
      },
    });
  };

  const calculateTotalPrices = (details) => {
    const totals = {};
    Object.keys(details).forEach((speedKey) => {
      let sum = 0;
      details[speedKey].forEach((item) => {
        sum += item.price;
      });
      const formattedSum = priceFormatter(sum);
      totals[speedKey] = formattedSum;
    });
    return totals;
  };

  const totalPriceBySpeed = calculateTotalPrices(totalValueDetails);

  return (
    <FlipCard flipped={flipped} isFlipCard={isFlipCard}>
      <>
        <PlanCardStyled
          ref={cardRef}
          is7Gig={is7Gig}
          className={`${featuredTheme}${featuredCard ? ' featured' : ''}${is7Gig ? ' new' : ''}${
            selectedCard ? ' selected' : ''
          } ${bannerEnabled && 'hasBanner'}`}
          data-testid={slugify(name)}
          expanded={expanded}
          expandedIncludedValue={expandedIncludedValue}
          flipped={flipped}
          hasFeatureSections={hasFeatureSections}
          skipReward={sweetener.value === 'skip'}
          isQuizResults={quiz?.results}
          showOuterBanner={
            (indianaOffer || metadata?.[dataspeed?.downloadInMbps]?.outerBanner?.outerBannerText) &&
            !quiz?.results &&
            showPlanCardBanner
          }
          outerBannerBackground={metadata?.[dataspeed?.downloadInMbps]?.outerBanner?.outerBannerBackground || 'gravity'}
          netflixWifiBundleToggled={netflixWifiBundleToggled}
          showInnerBanner={
            metadata?.[dataspeed?.downloadInMbps]?.innerBanner?.innerBannerText && !quiz?.results && showPlanCardBanner
          }
          innerBannerBackground={innerBannerBackground}
        >
          {(indianaOffer || metadata?.[dataspeed?.downloadInMbps]?.outerBanner?.outerBannerText) &&
            !quiz?.results &&
            showPlanCardBanner && (
              <div className={`plan-card__banner-outer ${outerBannerBackground}`}>
                <div {...setHtml(outerBannerText())} />
              </div>
            )}

          {metadata?.[dataspeed?.downloadInMbps]?.innerBanner?.innerBannerText &&
            !quiz?.results &&
            showPlanCardBanner && (
              <div className={`plan-card__banner-inner ${innerBannerBackground}`}>
                {innerBanner?.innerBannerIcon !== 'none' && (
                  <IconInnerBanner className="plan-card__banner-inner-icon" fill="red" />
                )}
                <div {...setHtml(innerBannerText())} />
              </div>
            )}

          <div className="plan-card__padded">
            <header
              className={!innerBanner ? 'plan-card__header' : 'plan-card__headerWithInnerBanner'}
              data-testid="plan-card-header"
            >
              <div className="plan-card__featured-header">
                {Object.keys(sweeteners).length > 0 && (
                  <div className="plan-card__banner-wrapper" data-testid="banner-hover">
                    <PlanCardBanner
                      legalMap={legalMap}
                      sweeteners={sweeteners}
                      sweetener={sweetener}
                      is200={is200}
                      is500={is500}
                      is1Gig={is1Gig}
                      is2Gig={is2Gig}
                      is5Gig={is5Gig}
                      is7Gig={is7Gig}
                    />
                  </div>
                )}
                {featuredCard && (bannerDetails?.bannerText === 'none' || !bannerDetails?.bannerText) && (
                  <div className="plan-card__best-value" data-testid="plan-card-best-value">
                    {String(featuredTheme) === 'theme-1' && <Star />}
                    <span>Best value</span>
                  </div>
                )}
                {is7Gig && (
                  <div className="plan-card__best-value" data-testid="plan-card-best-value">
                    <span>New</span>
                  </div>
                )}
                {bannerEnabled && bannerDetails?.bannerText && bannerDetails?.bannerText !== 'none' && (
                  <div className="plan-card__limited-time">
                    {bannerDetails?.bannerIcon !== 'none' && (
                      <div className="plan-card__limited-time-icon">{<Icon />}</div>
                    )}
                    {bannerDetails?.bannerText !== 'none' && (
                      <div className="plan-card__limited-time-bannerText">{bannerDetails?.bannerText}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="plan-card__speed">
                {!isCopper && dataspeed ? (
                  <Fragment>
                    {metadata?.[dataspeed?.downloadInMbps]?.name || name.replace('Internet', '').trim()}
                    <small>
                      {dataspeed?.downloadInMbps}/{dataspeed?.uploadInMbps} Mbps
                    </small>
                  </Fragment>
                ) : (
                  <Fragment>
                    Frontier
                    <br />
                    Internet
                  </Fragment>
                )}
              </div>

              {netflixWifiBundleToggled && (
                <div className="plan-card__netflix-bundle">
                  {!is7Gig ? (
                    <>
                      <p className="plan-card__netflix-bundle-name">
                        <img src={getAsset(`/images/logos/netflix-logo.svg`)} alt="NetFlix Pro Wi-Fi Bundle" />& Pro
                        Wi-Fi Bundle
                      </p>
                      <p className="plan-card__netflix-bundle-description">
                        Includes: Netflix Premium, Whole-Home Wi-Fi and Wi-Fi Security.{' '}
                        <button onClick={handleLearnMoreClick}>Learn more</button>
                      </p>
                    </>
                  ) : (
                    <p className="plan-card__netflix-bundle-description">
                      This product is only available as a fiber plan.
                    </p>
                  )}
                </div>
              )}

              <div
                className={`plan-card__price-container ${
                  netflixWifiBundleToggled ? 'plan-card__price-container--margin-top' : ''
                }`}
              >
                <div className="plan-card__price">
                  <sup className="pre">$</sup>
                  <span className="plan-card__dollars" data-testid="plan-card-amount-major">
                    {`${addMoney(
                      price.amountMajor,
                      netflixWifiBundleToggled && !is7Gig ? bundleCost + netflixWifiBundle?.price?.amount : bundleCost
                    )}`}
                  </span>
                </div>
                <div className="plan-card__cents">
                  <span data-testid="plan-card-amount-minor">{price.amountMinor}</span>{' '}
                  <Tooltip
                    position="top"
                    theme="light"
                    html={
                      <div>
                        <div className="tooltip-info">
                          {getTooltip(product, legalMap, Boolean(indianaOffer), addressOfferType)}
                        </div>
                      </div>
                    }
                  >
                    <InfoCircle />
                  </Tooltip>
                </div>
                <div className="plan-card__full-price" data-testid="plan-card-full-price">
                  $
                  {addMoney(
                    price.originalAmount,
                    netflixWifiBundleToggled && !is7Gig ? bundleCost + netflixWifiBundle?.price?.amount : bundleCost
                  )}
                </div>
              </div>

              <div className="plan-card__legal" ref={legalWrapperRef} style={{ height: maxLegalHeight || 'auto' }}>
                {shortLegal && (
                  <div className="plan-card__legal-text" data-testid="plan-card-legal-text" ref={legalRef}>
                    {getShortLegal(product, Boolean(indianaOffer), false, netflixWifiBundleToggled)?.replace(
                      '. ETF applies',
                      ''
                    )}
                    {netflixWifiBundleToggled && !is7Gig && <LegalAnnotation legalKey="netflixWifiBundle" />}
                    {quiz?.results && (
                      <>
                        <br />
                        <button className="consumer-label" onClick={() => setBroadbandConsumerLabelModal(product)}>
                          Broadband Consumer Label
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <PlanCardCTAs
                name={name}
                index={index}
                product={product}
                isCopper={isCopper}
                buttons={buttons}
                selectedPlan={selectedPlan}
                callInNumber={callInNumber}
                handleSelectPlan={handleSelectPlan}
                setSelectedCardIndex={setSelectedCardIndex}
                featured={featuredCard}
              />
            </header>
            {bestFor?.length && bestFor !== '__EMPTY__' && (
              <div className="plan-card__feature-section">
                <strong className="header">Best for</strong>
                <p>{bestFor}</p>
              </div>
            )}
            {!isFlipCard && (
              <div
                className="plan-card__features-container"
                style={{
                  height:
                    (expanded || expandedIncludedValue ? maxFeatureExpandedHeight : maxFeatureCollapsedHeight) ||
                    (hasFeatureSections ? 0 : 'auto'),
                }}
              >
                {/* SHOWS BEST FOR + WHAT'S INCLUDED CONTENT FROM PREAMP */}
                {hasFeatureSections ? (
                  <div className="plan-card__features" ref={featureRef}>
                    <div className={`plan-card__feature-section hidden${expanded ? ' expanded' : ''}`}>
                      <div className="header">
                        <strong>What's included</strong>
                        {sweeteners?.vrc && productFilters && (
                          <ProductFilters
                            addPhoneState={productFilters?.addPhoneState}
                            voiceAvailable={productFilters?.voiceAvailable}
                            handleAddHomePhone={productFilters?.handleAddHomePhone}
                            sweetener={productFilters?.sweetener}
                            setSweetener={productFilters?.setSweetener}
                            options={[
                              { label: 'Claim Rewards', value: 'visa' },
                              { label: 'Skip Rewards', value: 'skip' },
                            ]}
                          />
                        )}
                      </div>
                      {featureList?.map(
                        (feature, index) =>
                          // Hides VRC if 'Skip Rewards' is selected
                          !(
                            feature?.text?.toLowerCase()?.includes('visa') &&
                            products?.planPageSelections?.selectedSweetener?.value === 'skip'
                          ) && (
                            <FeatureItem
                              key={index}
                              index={index}
                              {...feature}
                              expanded={expanded}
                              setEeroModal={() => setEeroModal(product)}
                              isCopper={isCopper}
                              product={product}
                              is200={is200}
                              is500={is500}
                              is1Gig={is1Gig}
                              is2Gig={is2Gig}
                              is5Gig={is5Gig}
                              is7Gig={is7Gig}
                            />
                          )
                      )}
                      {!quiz?.results && (
                        <li className={`consumer-label plan-card__feature hidden ${expanded ? 'expanded' : ''}`}>
                          <button onClick={() => setBroadbandConsumerLabelModal(product)}>
                            Broadband Consumer Label
                          </button>
                        </li>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* SHOWS BULLETED LIST OF FEATURES */}
                    <ul className="plan-card__features" ref={featureRef}>
                      {features?.slice(0, expanded ? features.length : 3)?.map((feature, index) => (
                        <FeatureItem
                          key={index}
                          index={index}
                          {...feature}
                          expanded={expanded}
                          setEeroModal={() => setEeroModal(product)}
                          isCopper={isCopper}
                          product={product}
                          is200={is200}
                          is500={is500}
                          is1Gig={is1Gig}
                          is2Gig={is2Gig}
                          is5Gig={is5Gig}
                          is7Gig={is7Gig}
                          displayIcon={true}
                        />
                      ))}

                      {/* INCLUDED VALUE */}
                      {totalValueEnabled === 'true' && (
                        <>
                          <li
                            className={`consumer-label plan-card__feature--included-value ${
                              expandedIncludedValue ? 'expanded' : ''
                            }`}
                          >
                            <button onClick={() => toggleVisibility('includedValues')}>
                              <span className="heading">Included value:*</span>
                              <span>
                                <span className="heading">${totalPriceBySpeed?.[dataspeed?.downloadInMbps]}</span>
                                <ChevronDown aria-label="expand" />
                              </span>
                            </button>
                            {!expanded && !expandedIncludedValue && (
                              <p className="disclosure">*Total included value and discounts in the first 12 months</p>
                            )}
                          </li>
                          {expandedIncludedValue &&
                            totalValueDetails?.[dataspeed?.downloadInMbps]?.map((item, index) => (
                              <li
                                key={index}
                                className={`consumer-label plan-card__feature plan-card__feature--included-value hidden ${
                                  expandedIncludedValue ? 'expanded' : ''
                                }`}
                              >
                                <span>{item.name}</span>
                                <span>${item.price}</span>
                              </li>
                            ))}
                          {(expanded || expandedIncludedValue) && (
                            <li className="consumer-label plan-card__feature plan-card__feature--included-value disclosure expanded">
                              *Total included value and discounts in the first 12 months
                            </li>
                          )}
                        </>
                      )}
                      <li className={`consumer-label plan-card__feature hidden ${expanded ? 'expanded' : ''}`}>
                        <button onClick={() => setBroadbandConsumerLabelModal(product)}>
                          Broadband Consumer Label
                        </button>
                      </li>
                    </ul>
                    {sweeteners?.vrc && productFilters && (
                      <ProductFilters
                        addPhoneState={productFilters?.addPhoneState}
                        voiceAvailable={productFilters?.voiceAvailable}
                        handleAddHomePhone={productFilters?.handleAddHomePhone}
                        sweetener={productFilters?.sweetener}
                        setSweetener={productFilters?.setSweetener}
                        options={[
                          { label: 'Claim Rewards', value: 'visa' },
                          { label: 'Skip Rewards', value: 'skip' },
                        ]}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {isFlipCard ? (
              <button onClick={() => toggleVisibility('features')} className="plan-card__feature-toggle">
                <span className="toggle-text">
                  {hasFeatureSections && !isFlipCard ? <>What's included</> : <>More details</>}
                </span>
                <ChevronRight aria-label="flip" />
              </button>
            ) : (
              !quiz?.results && (
                <button onClick={() => toggleVisibility('features')} className="plan-card__feature-toggle">
                  <span className="toggle-text">
                    {hasFeatureSections ? (
                      <>{expanded ? 'Close' : `What's included`}</>
                    ) : (
                      <>{expanded ? 'Less' : 'More'} plan details</>
                    )}
                  </span>
                  <ChevronDown aria-label="expand" />
                </button>
              )
            )}
          </div>
          {indianaOffer && quiz?.results && (
            <div className="plan-card__footer-banner">
              Free Whole-Home Wi-Fi
              <br />
              included for 3 months!
            </div>
          )}
        </PlanCardStyled>

        {isFlipCard && (
          <PlanCardStyled
            className={`${featuredTheme}${selectedCard ? ' selected' : ''} ${isFlipCard ? 'backCard' : ''}`}
            data-testid={slugify(name)}
            expanded={expanded}
            flipped={flipped}
            hasFeatureSections={hasFeatureSections}
            skipReward={sweetener.value === 'skip'}
          >
            <header className="plan-card__header" data-testid="plan-card-header">
              <div className="plan-card__speed">
                {!isCopper && dataspeed ? (
                  <Fragment>
                    {metadata?.[dataspeed?.downloadInMbps]?.name || name.replace('Internet', '').trim()}
                    <small>
                      {dataspeed?.downloadInMbps}/{dataspeed?.uploadInMbps} Mbps
                    </small>
                  </Fragment>
                ) : (
                  <Fragment>
                    Frontier
                    <br />
                    Internet
                  </Fragment>
                )}
              </div>

              <div className="plan-card__legal" ref={legalWrapperRef} style={{ height: maxLegalHeight || 'auto' }}>
                {shortLegal && (
                  <div className="plan-card__legal-text" data-testid="plan-card-legal-text" ref={legalRef}>
                    {}
                  </div>
                )}
              </div>
            </header>

            {bestFor?.length && bestFor !== '__EMPTY__' && (
              <div className="plan-card__feature-section">
                <strong className="header">Best For</strong>
                <p>{bestFor}</p>
              </div>
            )}

            <div
              className="plan-card__features-container"
              style={{
                height:
                  (expanded && !isFlipCard ? maxFeatureExpandedHeight : maxFeatureCollapsedHeight) ||
                  (hasFeatureSections && !isFlipCard ? 0 : 'auto'),
              }}
            >
              {hasFeatureSections ? (
                <div className="plan-card__features" ref={featureRef}>
                  <div className={`plan-card__feature-section`}>
                    {featureList?.map((feature, index) => (
                      <FeatureItem
                        key={index}
                        index={index}
                        {...feature}
                        expanded={expanded}
                        displayIcon={true}
                        setEeroModal={() => setEeroModal(product)}
                        isCopper={isCopper}
                        product={product}
                        is200={is200}
                        is500={is500}
                        is1Gig={is1Gig}
                        is2Gig={is2Gig}
                        is5Gig={is5Gig}
                        is7Gig={is7Gig}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <ul className="plan-card__features" ref={featureRef}>
                  {features?.map((feature, index) => (
                    <FeatureItem
                      key={index}
                      index={index}
                      {...feature}
                      expanded={expanded}
                      setEeroModal={() => setEeroModal(product)}
                      isCopper={isCopper}
                      product={product}
                      is200={is200}
                      is500={is500}
                      is1Gig={is1Gig}
                      is2Gig={is2Gig}
                      is5Gig={is5Gig}
                      is7Gig={is7Gig}
                      displayIcon={true}
                    />
                  ))}
                </ul>
              )}
            </div>
            <PlanCardCTAs
              name={name}
              index={index}
              product={product}
              isCopper={isCopper}
              buttons={buttons}
              selectedPlan={selectedPlan}
              callInNumber={callInNumber}
              handleSelectPlan={handleSelectPlan}
              setSelectedCardIndex={setSelectedCardIndex}
              featured={featuredCard}
            />
            <button onClick={() => toggleVisibility('features')} className="plan-card__feature-toggle">
              <ChevronLeft aria-label="expand" />
              <span className="toggle-text">
                {hasFeatureSections && !isFlipCard ? <>&#8203; Close</> : <>&#8203; Less details</>}
              </span>
            </button>
          </PlanCardStyled>
        )}
      </>
    </FlipCard>
  );
};

PlanCard.displayName = 'PlanCard';

export default PlanCard;
